import { type Ref } from 'react'
import { Box, Flex, Text, VStack, useT } from '@minetrace/ui'
import { useFormColors, FormSection, TrackedSelect, withFormProvider, useFormContext } from '@minetrace/ui/forms'
import {
  LuMountain as Mountain,
  LuInfo as Info,
} from 'react-icons/lu'
import { MINING_CATEGORIES } from '../../../data/enums'
import { useStep2AccreditationForm, type Step2AccreditationFormValues } from './useStep2AccreditationForm'

const CATEGORY_OPTIONS = MINING_CATEGORIES.filter((opt) => opt.value !== 0)

// ── Límites legales por categoría (observación 4) ──
// Solo informativo: el sistema NO valida la capacidad declarada contra estos topes.
const T_UP_TO = 'proveedores.wizard.step2.limitsUpTo'
const T_MORE_THAN = 'proveedores.wizard.step2.limitsMoreThan'
const T_NO_LIMIT = 'proveedores.wizard.step2.limitsNoLegalLimit'
const T_NONE = 'proveedores.wizard.step2.limitsNone'

interface LimitCell {
  tid: string
  value?: string
}

interface CategoryLimits {
  metallic: LimitCell
  nonMetallic: LimitCell
  placer: LimitCell
  area: LimitCell
  eiaNote: boolean
}

const CATEGORY_LIMITS: Record<number, CategoryLimits> = {
  1: {
    // Gran Minería
    metallic: { tid: T_MORE_THAN, value: '5,000 TM/día' },
    nonMetallic: { tid: T_MORE_THAN, value: '5,000 TM/día' },
    placer: { tid: T_NONE },
    area: { tid: T_NO_LIMIT },
    eiaNote: true,
  },
  2: {
    // Mediana Minería
    metallic: { tid: T_UP_TO, value: '5,000 TM/día' },
    nonMetallic: { tid: T_UP_TO, value: '5,000 TM/día' },
    placer: { tid: T_MORE_THAN, value: '3,000 m³/día' },
    area: { tid: T_NO_LIMIT },
    eiaNote: true,
  },
  3: {
    // Pequeño Productor Minero (PPM)
    metallic: { tid: T_UP_TO, value: '350 TM/día' },
    nonMetallic: { tid: T_UP_TO, value: '1,200 TM/día' },
    placer: { tid: T_UP_TO, value: '3,000 m³/día' },
    area: { tid: T_UP_TO, value: '2,000 ha' },
    eiaNote: false,
  },
  4: {
    // Productor Minero Artesanal (PMA)
    metallic: { tid: T_UP_TO, value: '25 TM/día' },
    nonMetallic: { tid: T_UP_TO, value: '100 TM/día' },
    placer: { tid: T_UP_TO, value: '200 m³/día' },
    area: { tid: T_UP_TO, value: '1,000 ha' },
    eiaNote: false,
  },
}

const LIMIT_ROWS: { key: keyof Omit<CategoryLimits, 'eiaNote'>; labelTid: string }[] = [
  { key: 'metallic', labelTid: 'proveedores.wizard.step2.limitsMetallic' },
  { key: 'nonMetallic', labelTid: 'proveedores.wizard.step2.limitsNonMetallic' },
  { key: 'placer', labelTid: 'proveedores.wizard.step2.limitsPlacer' },
  { key: 'area', labelTid: 'proveedores.wizard.step2.limitsArea' },
]

interface Step2AccreditationProps {
  mode: 'edit' | 'readonly'
  defaultCategoryId?: number
  // Ref for parent to access form methods (trigger, getValues)
  ref?: Ref<unknown>
}

interface EstratoMineroInnerProps {
  formMethods: ReturnType<typeof useStep2AccreditationForm>
  mode: 'edit' | 'readonly'
}

const EstratoMineroInnerRaw = ({
  mode,
}: EstratoMineroInnerProps) => {
  const c = useFormColors()
  const { t } = useT()
  const isReadonly = mode === 'readonly'
  const { watch } = useFormContext<Step2AccreditationFormValues>()

  const categoryId = watch('categoryId')
  const limits = categoryId ? CATEGORY_LIMITS[categoryId] : null

  const renderLimit = (cell: LimitCell) => t(cell.tid, cell.value ? { value: cell.value } : undefined)

  return (
    <FormSection
      icon={Mountain}
      titleTid="proveedores.wizard.step2.stratumTitle"
      descriptionTid="proveedores.wizard.step2.stratumDescription"
    >
      <Box maxW={{ base: '100%', md: '420px' }}>
        <TrackedSelect
          name="categoryId"
          labelTid="proveedores.wizard.step2.categoryLabel"
          options={CATEGORY_OPTIONS}
          placeholderTid="proveedores.wizard.step2.categoryPlaceholder"
          disabled={isReadonly}
        />
      </Box>

      {limits && (
        <Box mt={4} p={4} bg={`${c.accent}10`} borderRadius="lg">
          <Flex gap={2} alignItems="center" mb={3}>
            <Box color={c.accent}>
              <Info size={16} />
            </Box>
            <Text tid="proveedores.wizard.step2.limitsPanelTitle" fontSize="sm" fontWeight="700" color={c.text} />
          </Flex>
          <VStack gap={2} alignItems="stretch">
            {LIMIT_ROWS.map((row) => (
              <Flex key={row.key} justifyContent="space-between" gap={4} alignItems="baseline">
                <Text tid={row.labelTid} fontSize="sm" color={c.textMuted} />
                <Text fontSize="sm" fontWeight="600" color={c.text} textAlign="right">
                  {renderLimit(limits[row.key])}
                </Text>
              </Flex>
            ))}
          </VStack>
          {limits.eiaNote && (
            <Text
              tid="proveedores.wizard.step2.limitsEiaNote"
              fontSize="xs"
              color={c.textMuted}
              mt={3}
              display="block"
            />
          )}
        </Box>
      )}
    </FormSection>
  )
}

const EstratoMineroInner = withFormProvider(EstratoMineroInnerRaw, useStep2AccreditationForm)

const Step2Accreditation = ({
  ref,
  mode,
  defaultCategoryId,
}: Step2AccreditationProps) => {
  // El estado REINFO/formalización dejó de mostrarse a nivel de proveedor: ahora
  // es por Derecho Minero (badge "Formalizado" / "En vías de formalización" en la
  // pantalla de Derechos Mineros). Acá solo queda el Estrato Minero (categoría).
  return (
    <VStack gap={6} alignItems="stretch">
      <EstratoMineroInner
        ref={ref}
        mode={mode}
        defaultValues={{
          categoryId: defaultCategoryId ?? 0,
        }}
      />
    </VStack>
  )
}

export default Step2Accreditation
